import { get } from "lodash";

/**
 * Extracts all parent tag IDs from a tag based on its type configuration
 * @param {Object} tag - The tag object
 * @param {Object} tagTypesConfig - The tag types configuration
 * @returns {Array} Array of parent tag IDs
 */
export const getAllParentTagIds = (tag, tagTypesConfig) => {
  if (!tag || !tagTypesConfig) {
    return [];
  }
  const typeConfig = tagTypesConfig[tag.type];
  if (!typeConfig) {
    return [];
  }
  const parentTagAttrs = get(typeConfig, "attributes.parentTag", []);
  return parentTagAttrs
    .filter(Boolean) // Filter out null/undefined attributes
    .map((attr) => tag.attributes?.[attr.id]?.trim())
    .filter(Boolean);
};

/**
 * Creates stats configuration for tag profile header
 * @param {Object} tag - The tag object
 * @param {Object} totals - The totals object with groups and people counts
 * @param {Object} tagTypesConfig - The tag types configuration for getting child tag type names
 * @param {boolean} isTagTypeNode - Whether this is a tag type node
 * @param {Array} tags - All tags for tag type nodes
 * @returns {Array} Array of stats configuration objects
 */
export const createStatsConfig = (
  tag,
  totals,
  tagTypesConfig,
  isTagTypeNode,
  tags
) => {
  const stats = [];

  // Only add child count display when called with TagsExplore context parameters
  if (tag && tagTypesConfig !== undefined) {
    if (isTagTypeNode && tags) {
      // For tag type nodes, show the total count of tags of this type
      const tagTypeName = get(
        tagTypesConfig,
        [tag.type, "name"],
        tag.type || "Tag"
      );
      const count = tags.length;

      // Only add stats if count > 0
      if (count > 0) {
        const label = `${count} ${tagTypeName}${count !== 1 ? "s" : ""}`;
        stats.push({
          id: "childTags",
          isChildCount: true, // Flag to indicate this should use ChildCountDisplay
          label, // Pre-calculated label for tag type nodes
          popoverContent: null,
        });
      }
    } else {
      // For regular tags, let ChildCountDisplay calculate from the tag
      stats.push({
        id: "childTags",
        isChildCount: true, // Flag to indicate this should use ChildCountDisplay
        label: "", // ChildCountDisplay renders the label
        tag, // Pass the tag for ChildCountDisplay to calculate counts
      });
    }
  }

  // Use totals if provided (for backward compatibility with old TagProfileHeader)
  if (totals?.groups) {
    const label = totals.groups === 1 ? "team" : "teams";
    stats.push({
      id: "teams",
      icon: ["fas", "users"],
      count: totals.groups,
      label: `${totals.groups} ${label} tagged`,
    });
  } else if (tag?.teamCount) {
    stats.push({
      id: "teams",
      icon: ["fas", "users"],
      count: tag.teamCount,
      label: `${tag.teamCount} Team${tag.teamCount === 1 ? "" : "s"}`,
    });
  }

  if (totals?.people) {
    const label = totals.people === 1 ? "person" : "people";
    stats.push({
      id: "people",
      icon: ["fas", "user"],
      count: totals.people,
      label: `${totals.people} ${label} tagged`,
    });
  } else if (tag?.memberCount) {
    stats.push({
      id: "people",
      icon: ["fas", "user"],
      count: tag.memberCount,
      label: `${tag.memberCount} Person${tag.memberCount === 1 ? "" : "s"}`,
    });
  }

  return stats;
};

/**
 * Creates badge configuration for tag profile header
 * @param {Object} tag - The tag object
 * @param {number} parentTagCount - The number of parent tags
 * @returns {Array|null} Array of badge configuration objects or null
 */
export const createBadgeConfig = (tag, parentTagCount) => {
  if (!tag) {
    return null;
  }

  const badges = [
    {
      id: tag.type,
      label: tag.tagTypeName || tag.type,
      color: "default",
    },
  ];

  if (parentTagCount > 1) {
    badges.push({
      id: "multiple-parents",
      label: `${parentTagCount} parents`,
      color: "default",
    });
  }

  return badges;
};
